import { formatDate } from '@hqo/date-time/core';
import { formatLocaleTimeOrEmpty } from 'app/shared/utils/format-locale-time';
import { useIntl } from 'react-intl';
import { TenantContentData } from 'app/data/types/content';
import { useBuilding } from 'app/shared/contexts/building';
import { useCallback } from 'react';

interface Props {
  attachable: TenantContentData;
  isConfirmedModalVisible: boolean;
  isClaimable: boolean;
  updateEventPostStatus: string;
  updateOfferStatus: string;
  showCancelTextOverride: boolean;
}

interface Response {
  buttonYesText: string;
  buttonCancelText: string;
  descriptionText: string;
  label: string;
  titleText: string;
  isButtonDisabled: boolean;
  isButtonHidden: boolean;
  cancelClaimButtonText: string;
  isFooterHidden: boolean;
}

export const useAttachableText = ({ attachable, isConfirmedModalVisible, isClaimable, updateEventPostStatus, updateOfferStatus, showCancelTextOverride }: Props): Response => {
  const intl = useIntl();
  const { building } = useBuilding();

  const showEventPostConfirmedModal = isConfirmedModalVisible && updateEventPostStatus === 'success';
  const showOfferConfirmedModal = isConfirmedModalVisible && updateOfferStatus === 'success';

  const getRsvpYesText: () => string = (): string =>
    (attachable.claims_details?.is_claimable ? intl.formatMessage({ id: 'rsvps.yes_RSVP' }) : intl.formatMessage({ id: 'buttons.yes_cancel' }));

  const getRsvpActionButtonText: () => string = (): string =>
    (isConfirmedModalVisible ? intl.formatMessage({ id: 'buttons.done' }) : getRsvpYesText());

  const getClaimYesText: () => string = (): string =>
    (isClaimable && !showCancelTextOverride ? intl.formatMessage({ id: 'claims.yes_claim' }) : intl.formatMessage({ id: 'buttons.yes_cancel' }));

  const getClaimActionButtonText: () => string = (): string =>
    (isConfirmedModalVisible ? intl.formatMessage({ id: 'buttons.done' }) : getClaimYesText());

  const getButtonYesText: () => string = (): string =>
    (attachable.rsvp_enabled ? getRsvpActionButtonText() : getClaimActionButtonText());

  const getButtonCancelText: () => string = (): string => intl.formatMessage({ id: 'buttons.cancel' });

  const getRsvpLabel = () => {
    if (attachable.claims_details && attachable.claims_details.user_count && attachable.claims_details.user_count > 0) {
      return intl.formatMessage({ id: 'rsvps.already_rsvpd' });
    }
    if (attachable.capacity && attachable.claims_details && attachable.claims_details.count && attachable.claims_details.count >= attachable.capacity) {
      return attachable.cta_label_at_capacity || intl.formatMessage({ id: 'rsvps.rsvp_full' });
    }
    return attachable.cta_label || '';
  };

  const getClaimLabel = () => {
    if ((attachable.claims_details && (attachable.claims_details.user_count ?? 0) === (attachable.max_claims_per_user ?? 1))) {
      return intl.formatMessage({ id: 'claims.already_claimed' });
    }
    if (attachable.max_claims_total && attachable.claims_details && attachable.claims_details.count && attachable.claims_details.count >= attachable.max_claims_total) {
      return attachable.cta_label_at_capacity || intl.formatMessage({ id: 'claims.claim_full' });
    }
    return attachable.cta_label || '';
  };

  const getLabel: () => string = (): string => {
    if (attachable.rsvp_enabled) {
      return getRsvpLabel();
    }
    if (attachable.claimable) {
      return getClaimLabel();
    }
    return attachable.cta_label || '';
  };

  const getIsButtonDisabled = () => {
    if (attachable.rsvp_enabled) {
      return !isClaimable && !attachable.claims_details?.user_count && attachable.capacity && attachable.claims_details?.count && attachable.claims_details.count >= attachable.capacity;
    }
    if (attachable.claimable) {
      return (!isClaimable && (attachable?.claims_details?.user_count ?? 0) !== (attachable.max_claims_per_user ?? 1)) || (!attachable.claims_details?.user_count && attachable.max_claims_total && attachable.claims_details?.count && attachable.claims_details.count >= attachable.max_claims_total);
    }

    return false;
  };

  const getIsFooterHidden = useCallback(() => {
    if (attachable.cta_display_start_at) {
      const ctaDisplayStartAt = new Date(attachable.cta_display_start_at);
      const ctaDisplayEndAt = new Date(attachable.cta_display_end_at ?? '');
      const currentDate = new Date();

      const isWithinRange = currentDate >= ctaDisplayStartAt && currentDate <= ctaDisplayEndAt;

      return !isWithinRange;
    }

    return false;
  }, [attachable.cta_display_end_at, attachable.cta_display_start_at]);

  const getIsButtonHidden = useCallback(() => {
    if ((attachable.claims_details?.user_count ?? 0) === (attachable.max_claims_per_user ?? 1)) {
      return true;
    }

    return false;
  }, [attachable.claims_details?.user_count, attachable.max_claims_per_user]);

  const getRsvpDescriptionText: () => string = (): string => {
    let id: string | null = null;
    if (attachable.claims_details?.is_claimable || showEventPostConfirmedModal) {
      id = showEventPostConfirmedModal ? 'rsvps.event_confirmed' : 'rsvps.confirm_RSVP_description_confirm';
    } else {
      id = 'rsvps.confirm_RSVP_description_cancel';
    }
    return intl.formatMessage(
      { id },
      {
        eventTitle: attachable.title,
        eventDate: attachable.start_at
          ? formatDate(attachable.start_at, {
              locale: intl.locale,
              timezone: building?.timezone || 'UTC',
              options: { month: 'long', day: 'numeric', year: 'numeric' },
            })
          : undefined,
        // formatLocaleTime rather than a raw `timeStyle: 'short'`: CLDR ships the `h` separator for
        // fr-CA but plain `13:45` for fr-FR, and it applies `h` to both so they agree on one screen.
        // Guarded: a truthy but UNPARSEABLE start_at reaches this, and the package's formatLocaleTime
        // throws RangeError on it where formatDate above quietly returns ''.
        eventTime: attachable.start_at
          ? formatLocaleTimeOrEmpty(new Date(attachable.start_at), intl.locale, building?.timezone || 'UTC')
          : undefined,
      },
    );
  };

  const getClaimDescriptionText: () => string = (): string => {
    if ((isClaimable || showOfferConfirmedModal) && !showCancelTextOverride) {
      return intl.formatMessage(
        { id: showOfferConfirmedModal ? 'claims.offer_confirmed' : 'claims.claim_offer_description' },
        { dealTitle: attachable.title },
      );
    }
    return intl.formatMessage({ id: 'claims.confirm_offer_description_cancel' }, { offerTitle: attachable.title });
  };

  const getDescription: () => string = (): string =>
    (attachable.rsvp_enabled ? getRsvpDescriptionText() : getClaimDescriptionText());

  const getRsvpTitleText: () => string = (): string => {
    if (attachable.claims_details?.is_claimable || showEventPostConfirmedModal) {
      return intl.formatMessage({ id: showEventPostConfirmedModal ? 'confirmed' : 'rsvps.confirm_RSVP' });
    }
    return intl.formatMessage({ id: 'are_you_sure' });
  };

  const getClaimTitleText: () => string = (): string => {
    if (attachable.claimable && showOfferConfirmedModal && !showCancelTextOverride) {
      return intl.formatMessage({ id: 'confirmed' });
    }
    return intl.formatMessage({ id: 'are_you_sure' });
  };

  const getTitleText: () => string = (): string => (attachable?.rsvp_enabled ? getRsvpTitleText() : getClaimTitleText());

  const getCancelClaimButtonText: () => string = (): string => {
    if (attachable.rsvp_enabled) {
      return intl.formatMessage({ id: 'rsvps.already_rsvpd' });
    }
    return intl.formatMessage({ id: 'claims.already_claimed' });
  };

  return {
    buttonYesText: getButtonYesText(),
    buttonCancelText: getButtonCancelText(),
    descriptionText: getDescription(),
    label: getLabel(),
    titleText: getTitleText(),
    isButtonDisabled: getIsButtonDisabled() || false,
    isButtonHidden: getIsButtonHidden(),
    isFooterHidden: getIsFooterHidden(),
    cancelClaimButtonText: getCancelClaimButtonText(),
  };
};
